import { useEffect, useRef, useState } from 'react'
import firebase from '../firebase/compat'
import { db } from '../firebase/config'
import { client } from './Client'
import notDeleted from '../helpers/notDeleted'

// AI-CONTEXT: See docs/features/livereport-widgets-housevisits-personas.md
// AI-CONTEXT: See docs/pitfalls.md#ui-empty-state-claims-absence-while-loading
//
// De enige plek waar `PersonaPresenceReports` over een periode wordt opgehaald.
// `usePersonaPresenceReports` (één maand) en `usePersonaPresenceYear` (een heel
// jaar) bouwen allebei alleen nog hun grenzen en laten het ophalen hier.
//
// De grenzen komen als milliseconden binnen en niet als `Date`: een `Date` is
// bij elke render een nieuw object, en dan draait het effect eindeloos door.
//
// Lezen gaat in één keer (`.get()`), niet met een listener: aanwezigheid is
// historie en verandert nauwelijks. `cancelled` vangt een antwoord op dat
// binnenkomt nadat de gebruiker al is weggeklikt.

/**
 * @typedef {object} PresenceRangeResult
 * @property {Array<object>} reports - De gevonden documenten
 * @property {boolean} loaded - Of het antwoord binnen is. Blijft `false` zolang
 *   er gehaald wordt, zodat een scherm 'nog niet bekend' niet als 'niets
 *   gevonden' toont — dat laatste is een bewering aan de lezer.
 * @property {Error|null} error - De fout als het ophalen mislukte. Zonder dit
 *   blijft `loaded` eeuwig `false` en leest een mislukte read als 'nog bezig':
 *   de gebruiker wacht dan op iets dat nooit komt.
 */

/**
 * @param {number|null|undefined} startMillis - Begin van de periode (inclusief)
 * @param {number|null|undefined} endMillis - Einde van de periode (inclusief)
 * @param {{ enabled?: boolean, personaId?: string|null }} [options] - Met
 *   `enabled: false` wordt er niet gehaald. Een eerder opgehaalde periode blijft
 *   dan staan: het scherm wisselt tussen maand en jaar, en opnieuw ophalen wat er
 *   al is kost bij een groot jaar duizenden documenten per klik.
 *   Met `personaId` beperkt de query zich tot die deelnemer (widget
 *   'Aanwezigheid' van één deelnemer, docs/features/livereport-per-deelnemer.md;
 *   index CompagnyID + PersonaID + Timestamp). Alleen dan mag een grens
 *   ontbreken (`null` = open): zonder deelnemer zou een open grens de hele
 *   tenant lezen, dus daar betekent een ontbrekende grens 'nog niet klaar'.
 * @returns {PresenceRangeResult}
 */
const usePersonaPresenceRange = (startMillis, endMillis, { enabled = true, personaId = null } = {}) => {
    const [state, setState] = useState({ reports: [], loaded: false, error: null })

    // Welke periode er in `state` zit. Alleen hierop wordt opnieuw opgehaald —
    // niet op `enabled`, want dat wisselt bij elke klik op Maand/Jaar terwijl de
    // gegevens in die seconden niet veranderd kunnen zijn.
    const loadedRange = useRef(null)

    useEffect(() => {
        if (!enabled) return undefined
        if (!personaId && (!startMillis || !endMillis)) return undefined

        const key = `${startMillis}-${endMillis}-${personaId || ''}`
        if (loadedRange.current === key) return undefined

        let cancelled = false
        loadedRange.current = key
        setState({ reports: [], loaded: false, error: null })

        let query = db.collection('PersonaPresenceReports').where('CompagnyID', '==', client)
        if (personaId) query = query.where('PersonaID', '==', personaId)
        if (startMillis) query = query.where('Timestamp', '>=', firebase.firestore.Timestamp.fromMillis(startMillis))
        if (endMillis) query = query.where('Timestamp', '<=', firebase.firestore.Timestamp.fromMillis(endMillis))

        query
            .get()
            .then((snapshot) => {
                if (cancelled) return
                setState({
                    // Een rapport in de prullenbak telt nergens mee (ADR-008); deze
                    // lees gaat buiten useFirestoreQuery om, dus filtert zelf.
                    reports: snapshot.docs
                        .map((doc) => ({ ...doc.data(), id: doc.id }))
                        .filter(notDeleted),
                    loaded: true,
                    error: null,
                })
            })
            .catch((error) => {
                // Opnieuw proberen mag: de periode is niet geladen.
                loadedRange.current = null
                console.error('usePersonaPresenceRange: ophalen mislukt', error)
                if (cancelled) return
                setState({ reports: [], loaded: false, error })
            })

        return () => {
            cancelled = true
        }
    }, [startMillis, endMillis, enabled, personaId])

    return state
}

export default usePersonaPresenceRange
